/**
 * Efficient API Client
 *
 * Features:
 * - In-memory caching with TTL
 * - Request deduplication (prevents duplicate in-flight requests)
 * - Rate limiting queue
 * - Retry with exponential backoff
 * - Request batching support
 * - Error message extraction from response body (v2)
 */

import { logger } from '@/utils/logger';

// ============================================================================
// Cache Implementation
// ============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ApiCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl ?? this.defaultTTL,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

// ============================================================================
// Request Deduplication
// ============================================================================

class RequestDeduplicator {
  private inFlight = new Map<string, Promise<unknown>>();

  async dedupe<T>(key: string, request: () => Promise<T>): Promise<T> {
    // If request is already in flight, return existing promise
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key) as Promise<T>;
    }

    // Create new request
    const promise = request().finally(() => {
      this.inFlight.delete(key);
    });

    this.inFlight.set(key, promise);
    return promise;
  }
}

// ============================================================================
// Rate Limiter (Token Bucket)
// ============================================================================

interface RateLimitConfig {
  maxTokens: number;
  refillRate: number; // tokens per second
  refillInterval: number; // ms
}

const RATE_LIMIT_TIMEOUT_MS = 30_000; // 30 seconds max wait in queue

class RateLimiter {
  private tokens: number;
  private maxTokens: number;
  private refillRate: number;
  private lastRefill: number;
  private intervalId: ReturnType<typeof setInterval>;
  private queue: Array<{
    resolve: () => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }> = [];

  constructor(config: RateLimitConfig) {
    this.maxTokens = config.maxTokens;
    this.tokens = config.maxTokens;
    this.refillRate = config.refillRate;
    this.lastRefill = Date.now();

    this.intervalId = setInterval(() => this.refill(), config.refillInterval);
  }

  destroy(): void {
    clearInterval(this.intervalId);
    // Reject all pending requests
    for (const request of this.queue) {
      clearTimeout(request.timer);
      request.reject(new Error('Rate limiter destroyed'));
    }
    this.queue = [];
  }

  private refill(): void {
    const now = Date.now();
    const elapsed = (now - this.lastRefill) / 1000;
    const tokensToAdd = elapsed * this.refillRate;

    this.tokens = Math.min(this.maxTokens, this.tokens + tokensToAdd);
    this.lastRefill = now;

    // Process queued requests
    while (this.queue.length > 0 && this.tokens >= 1) {
      const request = this.queue.shift();
      if (request) {
        clearTimeout(request.timer);
        this.tokens -= 1;
        request.resolve();
      }
    }
  }

  async acquire(): Promise<void> {
    if (this.tokens >= 1) {
      this.tokens -= 1;
      return;
    }

    // Queue the request with a timeout to prevent indefinite hangs
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        const idx = this.queue.findIndex(r => r.timer === timer);
        if (idx !== -1) this.queue.splice(idx, 1);
        reject(new Error('Rate limit queue timeout — request waited too long'));
      }, RATE_LIMIT_TIMEOUT_MS);

      this.queue.push({ resolve, reject, timer });
    });
  }

  getAvailableTokens(): number {
    return Math.floor(this.tokens);
  }
}

// ============================================================================
// Retry Logic
// ============================================================================

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryableStatuses: number[];
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

async function withRetry<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const { maxRetries, baseDelay, maxDelay, retryableStatuses } = {
    ...defaultRetryConfig,
    ...config,
  };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Check if we should retry
      const status = (error as { status?: number }).status;
      const shouldRetry =
        attempt < maxRetries &&
        status !== undefined &&
        retryableStatuses.includes(status);

      if (!shouldRetry) {
        throw error;
      }

      // Exponential backoff with jitter
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
        maxDelay
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// ============================================================================
// API Client
// ============================================================================

export interface ApiClientConfig {
  baseUrl: string;
  headers?: Record<string, string>;
  rateLimitConfig?: RateLimitConfig;
  cacheEnabled?: boolean;
  defaultCacheTTL?: number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  cache?: boolean;
  cacheTTL?: number;
  cacheKey?: string;
  dedupe?: boolean;
  retry?: boolean;
  skipRateLimit?: boolean;
}

export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private cache: ApiCache;
  private deduplicator: RequestDeduplicator;
  private rateLimiter: RateLimiter;
  private cacheEnabled: boolean;
  private defaultCacheTTL: number;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.defaultHeaders = config.headers ?? {};
    this.cacheEnabled = config.cacheEnabled ?? true;
    this.defaultCacheTTL = config.defaultCacheTTL ?? 5 * 60 * 1000;
    this.cache = new ApiCache();
    this.deduplicator = new RequestDeduplicator();
    this.rateLimiter = new RateLimiter(
      config.rateLimitConfig ?? {
        maxTokens: 10,
        refillRate: 1,
        refillInterval: 1000,
      }
    );
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      cache = method === 'GET',
      cacheTTL = this.defaultCacheTTL,
      cacheKey,
      dedupe = true,
      retry = true,
      skipRateLimit = false,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const key = cacheKey ?? `${method}:${url}:${JSON.stringify(body ?? {})}`;

    // Check cache first (only for GET requests by default)
    if (this.cacheEnabled && cache && this.cache.has(key)) {
      return this.cache.get<T>(key)!;
    }

    // Create the fetch function
    const fetchData = async (): Promise<T> => {
      // Rate limiting
      if (!skipRateLimit) {
        await this.rateLimiter.acquire();
      }

      const response = await fetch(url, {
        method,
        credentials: 'include', // Send session cookie
        headers: {
          'Content-Type': 'application/json',
          ...this.defaultHeaders,
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        // Try to read error details from response body
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.json();
          logger.log('[API Client] Error response body:', errorBody);
          if (errorBody.error) {
            errorMessage = errorBody.error;
          } else if (errorBody.message) {
            errorMessage = errorBody.message;
          }
        } catch (parseError) {
          logger.log('[API Client] Could not parse error body:', parseError);
        }
        logger.log('[API Client] Final error message:', errorMessage);
        const error = new Error(errorMessage);
        (error as Error & { status: number }).status = response.status;
        throw error;
      }

      return response.json();
    };

    // Execute with optional deduplication and retry
    let result: T;

    if (dedupe) {
      result = await this.deduplicator.dedupe(key, () =>
        retry ? withRetry(fetchData) : fetchData()
      );
    } else {
      result = retry ? await withRetry(fetchData) : await fetchData();
    }

    // Cache the result
    if (this.cacheEnabled && cache) {
      this.cache.set(key, result, cacheTTL);
    }

    return result;
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, cache: false, ...options });
  }

  async put<T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body, cache: false });
  }

  async patch<T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body, cache: false });
  }

  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE', cache: false });
  }

  // Cache management
  invalidateCache(pattern?: string): void {
    if (pattern) {
      this.cache.invalidatePattern(pattern);
    } else {
      this.cache.clear();
    }
  }

  // Rate limit info
  getAvailableRequests(): number {
    return this.rateLimiter.getAvailableTokens();
  }
}

// ============================================================================
// Singleton instances for Apollo and Monday
// ============================================================================

// API key for server auth (set via VITE_APP_API_KEY env var)
const apiKeyHeader: Record<string, string> = import.meta.env.VITE_APP_API_KEY
  ? { 'X-API-Key': import.meta.env.VITE_APP_API_KEY }
  : {};

export const apolloClient = new ApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: apiKeyHeader,
  rateLimitConfig: {
    maxTokens: 5, // Conservative limit for Apollo (50/min API limit)
    refillRate: 0.8, // ~48 per minute
    refillInterval: 1000,
  },
  defaultCacheTTL: 10 * 60 * 1000, // 10 min cache for Apollo data
});

export const mondayClient = new ApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: apiKeyHeader,
  rateLimitConfig: {
    maxTokens: 10, // Monday has higher limits
    refillRate: 1,
    refillInterval: 1000,
  },
  defaultCacheTTL: 60 * 60 * 1000, // 1 hour cache for Monday
});

// ============================================================================
// Export cache for direct access if needed
// ============================================================================

export { ApiCache, RateLimiter, withRetry };
